// pages/ResetPasswordPage.js —— 忘记密码 · 重置密码流程
// 流程（轮廓级演示，不做后端对接）：1 身份验证（手机号/邮箱 + 验证码）→ 2 设置新密码 → 3 完成
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobileOutlined, MailOutlined, CheckCircleFilled } from '@ant-design/icons';

const STEPS = ['身份验证', '重置密码', '完成'];

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [verifyType, setVerifyType] = useState('mobile');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [tip, setTip] = useState('');

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const sendCode = () => {
    if (!identifier.trim()) { setErrorMsg(`请先输入${verifyType === 'mobile' ? '手机号' : '邮箱'}`); return; }
    setErrorMsg('');
    setCountdown(60);
    setTip('验证码已发送：签名为【储能管家】的 4 位数字验证码（请输入任意 4 位数字）');
  };

  const toReset = () => {
    if (!identifier.trim()) { setErrorMsg(`请输入${verifyType === 'mobile' ? '手机号' : '邮箱'}`); return; }
    if (!/^\d{4}$/.test(code.trim())) { setErrorMsg('请输入 4 位数字验证码'); return; }
    setErrorMsg('');
    setTip('');
    setStep(1);
  };

  const toDone = () => {
    if (password.trim().length < 6) { setErrorMsg('密码长度至少 6 位'); return; }
    if (password !== confirm) { setErrorMsg('两次输入的密码不一致'); return; }
    setErrorMsg('');
    setStep(2);
  };

  const stepsBar = (
    <div className="jc-steps">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          {i > 0 ? <span className="jc-step-line" /> : null}
          <span className={`jc-step${i === step ? ' active' : ''}${i < step ? ' done' : ''}`}>
            <span className="dot">{i < step ? <CheckCircleFilled /> : i + 1}</span>
            {label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="login-page">
      <div className="lg-card jc-card">
        <div className="lg-form">
          <h2 className="lg-heading">重置密码</h2>
          <p className="lg-heading-sub">验证手机号或邮箱后设置新的登录密码</p>

          {stepsBar}

          {step === 0 ? (
            <>
              <div className="jc-seg">
                <button type="button" className={verifyType === 'mobile' ? 'active' : ''} onClick={() => { setVerifyType('mobile'); setErrorMsg(''); setTip(''); }}>
                  <MobileOutlined /> 手机号
                </button>
                <button type="button" className={verifyType === 'email' ? 'active' : ''} onClick={() => { setVerifyType('email'); setErrorMsg(''); setTip(''); }}>
                  <MailOutlined /> 邮箱
                </button>
              </div>

              <div className="lg-field">
                <label className="lg-label" htmlFor="rp-id"><span className="req">*</span>{verifyType === 'mobile' ? '手机号' : '邮箱'}</label>
                <input
                  id="rp-id"
                  className="lg-input"
                  placeholder={verifyType === 'mobile' ? '请输入注册手机号' : '请输入注册邮箱'}
                  value={identifier}
                  onChange={(e) => { setIdentifier(e.target.value); setErrorMsg(''); }}
                />
              </div>

              <div className="lg-field">
                <label className="lg-label" htmlFor="rp-code"><span className="req">*</span>验证码</label>
                <div className="lg-code-row">
                  <input
                    id="rp-code"
                    className="lg-input"
                    placeholder="请输入 4 位验证码"
                    maxLength={4}
                    value={code}
                    onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setErrorMsg(''); }}
                  />
                  <button type="button" className="lg-code-btn" disabled={countdown > 0} onClick={sendCode}>
                    {countdown > 0 ? `${countdown}s 后重发` : '获取验证码'}
                  </button>
                </div>
              </div>

              <div className="lg-error">{errorMsg || (tip ? <span className="lg-tip">{tip}</span> : '')}</div>

              <div className="jc-actions">
                <button type="button" className="jc-ghost" onClick={() => navigate('/')}>返回登录</button>
                <button type="button" className="lg-submit" onClick={toReset}>下一步</button>
              </div>
            </>
          ) : null}

          {step === 1 ? (
            <>
              <div className="lg-field">
                <label className="lg-label" htmlFor="rp-pwd"><span className="req">*</span>新密码</label>
                <input
                  id="rp-pwd"
                  className="lg-input"
                  type="password"
                  placeholder="请输入新密码（至少 6 位）"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrorMsg(''); }}
                />
              </div>
              <div className="lg-field">
                <label className="lg-label" htmlFor="rp-confirm"><span className="req">*</span>确认新密码</label>
                <input
                  id="rp-confirm"
                  className="lg-input"
                  type="password"
                  placeholder="请再次输入新密码"
                  value={confirm}
                  onChange={(e) => { setConfirm(e.target.value); setErrorMsg(''); }}
                />
              </div>

              <div className="lg-error">{errorMsg}</div>

              <div className="jc-actions">
                <button type="button" className="jc-ghost" onClick={() => { setStep(0); setErrorMsg(''); }}>上一步</button>
                <button type="button" className="lg-submit" onClick={toDone}>确认重置</button>
              </div>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <div className="jc-invalid" style={{ paddingBottom: 0 }}>
                <CheckCircleFilled className="ic" style={{ color: '#52e8b0' }} />
                <h3>密码重置成功</h3>
                <p>请使用新密码重新登录，建议妥善保管并定期更换密码。</p>
              </div>
              <button type="button" className="lg-submit" style={{ marginTop: 24 }} onClick={() => navigate('/')}>
                返回登录
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;